import React, { useEffect, useMemo, useState } from "react";

import { useQuery } from "@apollo/client";
import { SearchOutlined as SearchOutlinedIcon } from "@mui/icons-material";
import {
  Avatar,
  Checkbox,
  InputAdornment,
  ListItemAvatar,
  ListItemText,
  TextField,
  Autocomplete,
} from "@mui/material";

import { graphql } from "@/gql";
import { Maybe } from "@/gql/graphql";

interface Props {
  value: ClientRecipient[];
  onChange: (newValue: ClientRecipient[]) => void;
}

export interface ClientRecipient {
  client: {
    id: string;
    email?: Maybe<string> | undefined;
    profile?:
      | Maybe<{
          id: string;
          fullName: string;
          avatarUrl?: Maybe<string> | undefined;
        }>
      | undefined;
  } | null;
}

const clientListAutoCompleteQuery = graphql(`
  query ShareFormClients {
    clients {
      nodes {
        id
        email
        profile {
          id
          fullName
          avatarUrl(variant: THUMB)
        }
      }
    }
  }
`);

const ClientListAutoComplete: React.FC<Props> = ({ value, onChange }) => {
  const { data } = useQuery(clientListAutoCompleteQuery);
  const [currentValues, setCurrentValues] = useState<ClientRecipient[]>(
    value as ClientRecipient[]
  );

  const clientRecipients = useMemo<ClientRecipient[]>(
    () => data?.clients.nodes.map((n) => ({ client: n })) ?? [],
    [data]
  );

  useEffect(() => {
    setCurrentValues(value as ClientRecipient[]);
  }, [value]);

  return (
    <Autocomplete
      options={clientRecipients}
      value={currentValues as ClientRecipient[]}
      onChange={(_, newValue) => {
        setCurrentValues(newValue as ClientRecipient[]);
        onChange(newValue as ClientRecipient[]);
      }}
      isOptionEqualToValue={(option, value) =>
        option.client?.id === value.client?.id
      }
      multiple
      disableCloseOnSelect
      getOptionLabel={(option: ClientRecipient) => {
        return option.client?.profile?.fullName ?? "";
      }}
      renderOption={(props, option, { selected }) => {
        return (
          <li {...props}>
            <Checkbox checked={selected} color="primary" />
            <ListItemAvatar>
              <Avatar src={option.client?.profile?.avatarUrl ?? ""}></Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={option.client?.profile?.fullName}
              secondary={option.client?.email}
            />
          </li>
        );
      }}
      renderInput={(props) => (
        <TextField
          {...props}
          placeholder={"Add clients"}
          InputProps={{
            ...props.InputProps,
            className: "",
            endAdornment: (
              <InputAdornment position="end">
                <SearchOutlinedIcon />
              </InputAdornment>
            ),
          }}
        />
      )}
      renderTags={() => null}
    />
  );
};

export default ClientListAutoComplete;
